import React, { useCallback } from 'react'
import { SelectButton } from 'primereact/selectbutton'
import {
  Badge,
  CustomCalendar,
  CustomDropdown,
  CustomInputTextArea,
  InputText,
  InputTime,
  SectionTitle,
} from '../../../../../components'
import * as colors from '../../../../../assets/js/colors'
import { ResultadoResposta } from '../../../../../constants/checklist-operacional'
import { classificarResposta, formatarDataExibicao, SIM_NAO } from '../checklist.util'
import { progressoEtapa } from '../respostas.util'
import './ChecklistForm.css'

const MAX_OPCOES_EM_BOTOES = 5

const styles = {
  titulo: { color: colors.azul, margin: 0 },
  subtitulo: { color: colors.gray, fontSize: '0.8rem' },
  etapa: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  progresso: { color: colors.gray, fontSize: '0.8rem', whiteSpace: 'nowrap' },
  orientacoes: { whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: colors.gray, marginTop: 10 },
  label: { fontSize: '0.8rem', color: colors.azul, display: 'block', marginTop: 10 },
}

function Campo({ campo, valor, onChange, disabled }) {
  if (campo.field_type === 'dropdown') {
    const opcoes = campo.options || []

    return (
      <CustomDropdown
        label={`${campo.label}:*`}
        marginTop={10}
        optionLabel="label"
        disabled={disabled}
        options={opcoes}
        value={opcoes.find(opcao => opcao.id === valor) || null}
        onChange={value => onChange(value ? value.id : null)}
      />
    )
  }

  if (campo.field_type === 'checkbox') {
    return (
      <div>
        <label style={styles.label}>{campo.label}:*</label>
        <SelectButton
          value={valor}
          options={SIM_NAO}
          disabled={disabled}
          onChange={e => onChange(e.value)}
        />
      </div>
    )
  }

  if (campo.field_type === 'date') {
    return (
      <CustomCalendar
        label={`${campo.label}:*`}
        value={valor}
        disabled={disabled}
        onChange={onChange}
      />
    )
  }

  if (campo.field_type === 'time') {
    return (
      <InputTime
        label={`${campo.label}:*`}
        marginTop={10}
        value={valor || ''}
        disabled={disabled}
        onChange={onChange}
      />
    )
  }

  return (
    <InputText
      label={`${campo.label}:*`}
      marginTop={10}
      value={valor || ''}
      disabled={disabled}
      onChange={onChange}
    />
  )
}

function Resposta({ item, valor, onChange, disabled }) {
  if (item.field_type === 'dropdown') {
    const opcoes = item.options || []

    if (opcoes.length > MAX_OPCOES_EM_BOTOES) {
      return (
        <CustomDropdown
          showLabel={false}
          marginTop={0}
          optionLabel="label"
          disabled={disabled}
          options={opcoes}
          value={opcoes.find(opcao => opcao.id === valor) || null}
          onChange={value => onChange(value ? value.id : null)}
        />
      )
    }

    return (
      <SelectButton
        value={valor}
        options={opcoes.map(opcao => ({ label: opcao.label, value: opcao.id }))}
        disabled={disabled}
        onChange={e => onChange(e.value)}
      />
    )
  }

  if (item.field_type === 'checkbox') {
    return (
      <SelectButton
        value={valor}
        options={SIM_NAO}
        disabled={disabled}
        onChange={e => onChange(e.value)}
      />
    )
  }

  if (item.field_type === 'date') {
    return (
      <CustomCalendar
        label=""
        marginTop={0}
        value={valor}
        disabled={disabled}
        onChange={onChange}
      />
    )
  }

  if (item.field_type === 'time') {
    return (
      <InputTime
        label=""
        marginTop={0}
        value={valor || ''}
        disabled={disabled}
        onChange={onChange}
      />
    )
  }

  return (
    <InputText
      showLabel={false}
      marginTop={0}
      value={valor || ''}
      disabled={disabled}
      onChange={onChange}
    />
  )
}

function Classificacao({ resultado }) {
  if (!resultado) return null

  const { label, color } = ResultadoResposta[resultado]
  return <Badge color={color}>{label}</Badge>
}

const CampoLinha = React.memo(function CampoLinha({
  campo,
  valor,
  pendente,
  disabled,
  onFieldChange,
}) {
  return (
    <div
      className={`p-md-4 p-col-12 ChecklistForm__campo${pendente ? ' ChecklistForm__pendente' : ''}`}
      data-resposta-id={campo.id}
    >
      <Campo
        campo={campo}
        valor={valor}
        disabled={disabled}
        onChange={value => onFieldChange(campo.id, value)}
      />
    </div>
  )
})

const ItemLinha = React.memo(function ItemLinha({
  item,
  resposta,
  observacaoPorItem,
  observacaoLabel,
  pendente,
  disabled,
  mostrarClassificacao,
  classificacao,
  onItemChange,
}) {
  const valor = resposta ? resposta.value : null
  const observacao = resposta && resposta.observation ? resposta.observation : ''

  return (
    <tr
      className={`ChecklistForm__item${pendente ? ' ChecklistForm__pendente' : ''}`}
      data-resposta-id={item.id}
    >
      <td>{item.item_name}</td>
      <td className="ChecklistForm__col-resposta">
        <Resposta
          item={item}
          valor={valor}
          disabled={disabled}
          onChange={value => onItemChange(item.id, 'value', value)}
        />
      </td>
      {mostrarClassificacao ? (
        <td className="ChecklistForm__col-classificacao">
          <Classificacao resultado={classificacao} />
        </td>
      ) : null}
      {observacaoPorItem ? (
        <td className="ChecklistForm__col-observacao">
          <InputText
            showLabel={false}
            marginTop={0}
            placeholder={observacaoLabel}
            disabled={disabled}
            value={observacao}
            onChange={value => onItemChange(item.id, 'observation', value)}
          />
        </td>
      ) : null}
    </tr>
  )
})

function TabelaItens({
  etapa,
  respostas,
  pendentes,
  disabled,
  mostrarClassificacao,
  classificacoes,
  observacaoPorItem,
  observacaoLabel,
  onItemChange,
}) {
  const colunas = 2 + (mostrarClassificacao ? 1 : 0) + (observacaoPorItem ? 1 : 0)
  const classificacaoDe = (item, resposta) => {
    if (!mostrarClassificacao) return null
    if (classificacoes) return classificacoes[item.id] || null
    return classificarResposta(item, resposta ? resposta.value : null)
  }

  return (
    <table className="ChecklistForm__tabela">
      <thead>
        <tr>
          <th>Item de verificação</th>
          <th className="ChecklistForm__col-resposta">Resposta</th>
          {mostrarClassificacao ? (
            <th className="ChecklistForm__col-classificacao">Classificação</th>
          ) : null}
          {observacaoPorItem ? (
            <th className="ChecklistForm__col-observacao">{observacaoLabel}</th>
          ) : null}
        </tr>
      </thead>
      {etapa.groups.map(grupo => (
        <tbody key={grupo.id}>
          <tr className="ChecklistForm__grupo">
            <td colSpan={colunas}>{grupo.group_name || 'Grupo sem nome'}</td>
          </tr>
          {grupo.items.map(item => (
            <ItemLinha
              key={item.id}
              item={item}
              resposta={respostas.items[item.id]}
              observacaoPorItem={observacaoPorItem}
              observacaoLabel={observacaoLabel}
              pendente={!!pendentes && pendentes.has(item.id)}
              disabled={disabled}
              mostrarClassificacao={mostrarClassificacao}
              classificacao={classificacaoDe(item, respostas.items[item.id])}
              onItemChange={onItemChange}
            />
          ))}
        </tbody>
      ))}
    </table>
  )
}

function Campos({ campos, respostas, pendentes, disabled, onFieldChange }) {
  if (!campos.length) return null

  return (
    <div className="p-grid">
      {campos.map(campo => (
        <CampoLinha
          key={campo.id}
          campo={campo}
          valor={respostas.fields[campo.id]}
          pendente={!!pendentes && pendentes.has(campo.id)}
          disabled={disabled}
          onFieldChange={onFieldChange}
        />
      ))}
    </div>
  )
}

export default function ChecklistForm({
  checklist,
  respostas,
  onChange,
  somenteLeitura = false,
  pendentes = null,
  mostrarClassificacao = false,
  classificacoes = null,
  mostrarProgresso = false,
  mostrarTitulo = false,
}) {
  const onFieldChange = useCallback(
    (id, valor) => onChange(atual => ({ ...atual, fields: { ...atual.fields, [id]: valor } })),
    [onChange],
  )
  const onItemChange = useCallback(
    (id, chave, valor) =>
      onChange(atual => ({
        ...atual,
        items: {
          ...atual.items,
          [id]: { ...(atual.items[id] || { value: null, observation: null }), [chave]: valor },
        },
      })),
    [onChange],
  )
  const onObservationsChange = useCallback(
    valor => onChange(atual => ({ ...atual, observations: valor })),
    [onChange],
  )

  return (
    <div className="ChecklistForm">
      {mostrarTitulo ? (
        <>
          <h3 style={styles.titulo}>
            {checklist.checklist_code || 'Sem código'} - {checklist.title || 'Sem título'}
          </h3>
          <span style={styles.subtitulo}>
            {checklist.reviewed_at
              ? `Revisão: ${formatarDataExibicao(checklist.reviewed_at)}`
              : 'Sem data de revisão'}
          </span>
        </>
      ) : null}

      <Campos
        campos={checklist.header}
        respostas={respostas}
        pendentes={pendentes}
        disabled={somenteLeitura}
        onFieldChange={onFieldChange}
      />

      {checklist.body.map(etapa => {
        const progresso = mostrarProgresso ? progressoEtapa(etapa, respostas) : null

        return (
          <div key={etapa.id}>
            <div style={styles.etapa}>
              <div style={{ flex: 1 }}>
                <SectionTitle title={etapa.step_name || 'Etapa sem nome'} />
              </div>
              {progresso ? (
                <span style={styles.progresso}>
                  {progresso.respondidos} de {progresso.total} respondidos
                </span>
              ) : null}
            </div>
            {etapa.instructions ? <div style={styles.orientacoes}>{etapa.instructions}</div> : null}
            <TabelaItens
              etapa={etapa}
              respostas={respostas}
              pendentes={pendentes}
              disabled={somenteLeitura}
              mostrarClassificacao={mostrarClassificacao}
              classificacoes={classificacoes}
              observacaoPorItem={checklist.item_observations.enabled}
              observacaoLabel={checklist.item_observations.label}
              onItemChange={onItemChange}
            />
          </div>
        )
      })}

      {checklist.observations.enabled ? (
        <CustomInputTextArea
          label={checklist.observations.label}
          rows={3}
          disabled={somenteLeitura}
          value={respostas.observations || ''}
          onChange={onObservationsChange}
        />
      ) : null}

      <Campos
        campos={checklist.footer}
        respostas={respostas}
        pendentes={pendentes}
        disabled={somenteLeitura}
        onFieldChange={onFieldChange}
      />
    </div>
  )
}
